import React, { useState } from 'react';
import { View, Pressable, Text } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramFumehoodMultiPointAverage } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.fumehoodMultiPointAverage.v1';

type Fields = { points: string[] };
const EMPTY: Fields = { points: ['', '', '', '', '', ''] };

/**
 * Multi-Point Average — average of multiple face velocity readings.
 */
const FumehoodMultiPointAverageScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [average, setAverage] = useState<string>('');
  const [count, setCount] = useState<number>(0);
  const { unit, toImp, fromImp } = useUnits();

  const updatePoint = (idx: number, value: string) => {
    setFields((prev) => {
      const next = [...prev.points];
      next[idx] = value;
      return { points: next };
    });
  };

  const addPoint = () => setFields((prev) => ({ points: [...prev.points, ''] }));
  const removePoint = () =>
    setFields((prev) => ({ points: prev.points.length > 1 ? prev.points.slice(0, -1) : prev.points }));

  const calculate = () => {
    setError(null);
    const nums = fields.points
      .map((p) => (p === '' ? NaN : toImp(p, 'velocity')))
      .filter((n) => Number.isFinite(n)) as number[];

    if (nums.length === 0) {
      setError('Enter at least one velocity reading.');
      setAverage('');
      setCount(0);
      return;
    }

    const avg = nums.reduce((s, n) => s + n, 0) / nums.length;
    setAverage(fromImp(avg, 'velocity', 1));
    setCount(nums.length);
  };

  const clearAll = () => {
    setError(null);
    setAverage('');
    setCount(0);
    void clearPersisted();
  };

  const result = average
    ? ([
        { label: 'Points entered', value: String(count) },
        { label: 'Average', value: average, unit: unit('velocity'), emphasis: true as const },
      ] as { label: string; value: string; unit?: string; emphasis?: boolean }[])
    : null;

  return (
    <CalcShell
      title="Multi-Point Average"
      subtitle={`Enter multiple face velocity readings to calculate an average (${unit('velocity')})`}
      formula={
        <>
          <FormulaLine>Average = (V₁ + V₂ + … + Vₙ) / n</FormulaLine>
          <FormulaNote>Leave unused points blank. Only numeric points are averaged.</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result}
      copyFooter="Formula: Avg = Σ Vᵢ / n"
      diagram={<DiagramFumehoodMultiPointAverage />}
      outputSlots={{
        velocity_fpm: average ? toImp(average, 'velocity') : undefined,
      }}
    >
      {fields.points.map((value, idx) => (
        <View key={idx} style={{ marginBottom: 12 }}>
          <NumericField
            label={`Point ${idx + 1} (${unit('velocity')})`}
            value={value}
            onChangeText={(t) => updatePoint(idx, t)}
            rightText={unit('velocity')}
            maxDecimals={2}
            onSubmitEditing={calculate}
          />
        </View>
      ))}
      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 12 }}>
        <Pressable
          onPress={addPoint}
          style={{
            flex: 1,
            paddingVertical: 12,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: '#888',
            alignItems: 'center',
          }}
        >
          <Text style={{ color: '#333', fontWeight: '600' }}>Add Point</Text>
        </Pressable>
        <Pressable
          onPress={removePoint}
          disabled={fields.points.length <= 1}
          style={{
            flex: 1,
            paddingVertical: 12,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: '#888',
            alignItems: 'center',
            opacity: fields.points.length <= 1 ? 0.4 : 1,
          }}
        >
          <Text style={{ color: '#333', fontWeight: '600' }}>Remove</Text>
        </Pressable>
      </View>
    </CalcShell>
  );
};

export default FumehoodMultiPointAverageScreen;
